import * as Sentry from "@sentry/react";
import { bucketFromUrl, spanName, datesVariantSuffix } from "@/lib/telemetry/bucket";

// Prefer env var, fall back to the DSN you provided
const DSN =
  import.meta.env.VITE_SENTRY_WIDGET_DSN ||
  "https://1b3ae89dc153adfe7a66e504c4f8a04a@o4510267821981696.ingest.de.sentry.io/4510346784604240";

// For an embeddable widget we don't want host page navigations/page-loads,
// but we DO want XHR/Fetch spans to stitch Axios traces ↔ Sentry transactions.
Sentry.init({
  dsn: DSN,
  tracesSampleRate: 1.0,          // adjust in prod if needed
  profilesSampleRate: 0.5,        // optional (JS profiling)
  integrations: [
    Sentry.browserTracingIntegration({
      instrumentNavigation: false,
      instrumentPageLoad: false,
      instrumentFetch: true,
      instrumentXHR: true,
    }),
    Sentry.breadcrumbsIntegration({ console: false }),
  ],
  // Only propagate trace headers to your API origins (keep host sites clean)
  tracePropagationTargets: [
    /^https?:\/\/([a-z0-9-]+\.)*boona\.app\b/i,
  ],
  environment: import.meta.env.MODE,
  release:
    import.meta.env.VITE_SENTRY_WIDGET_RELEASE ||
    import.meta.env.VITE_SENTRY_RELEASE || undefined,

  // Keep the same HTTP span normalization you're using in main
  // so widget spans look identical in Sentry.
  beforeSendTransaction(event) {
    try {
      const spans = Array.isArray((event as any).spans) ? (event as any).spans : [];
      for (const s of spans) {
        const op = s.op || "";
        const isHttp =
          op === "resource.fetch" || op === "resource.xhr" || op === "http.client" || op === "http";
        if (!isHttp) continue;

        const method =
          (s.data && (s.data["http.method"] || s.data.method)) ||
          (typeof s.description === "string" ? s.description.split(" ")[0] : "GET");
        const url =
          (s.data && (s.data.url || s.data["http.url"])) ||
          (typeof s.description === "string" ? s.description.split(" ")[1] : "");
        if (!url) continue;

        const bucket = bucketFromUrl(url);
        const { suffix, count } = datesVariantSuffix(url, bucket);
        s.description = spanName(method, bucket) + suffix;
        s.data = {
          ...(s.data || {}),
          "http.route": bucket,
          "telemetry.bucket": bucket,
          ...(count !== undefined ? { "telemetry.dates.count": count } : {}),
        };
      }
    } catch {
      // never block sending on normalization issues
    }
    return event;
  },
});

// Useful tag to distinguish widget traffic
Sentry.setTag("app", "widget");
